import { Core } from "@faire/design-tokens";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web--source/common/localization";
import { Row } from "@faire/web--source/slate/Layout";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React from "react";
import styled, { keyframes } from "styled-components";

const getAnimationTypeSuggestions = (
  strictLocalize: StrictLocalizeFunction
) => {
  return [
    strictLocalize({
      defaultMessage: "“holiday decorations”",
      description: {
        text: "Placeholder for search input. The default message is the text within the quotes",
        img: "https://cdn.faire.com/fastly/7fc2f0584ac3d512ec932f9d1c3547acf2a50966e487acf011948334c71c5819.png",
      },
    }),
    strictLocalize({
      defaultMessage: "“dresses”",
      description: {
        text: "Placeholder for search input. The default message is the text within the quotes",
        img: "https://cdn.faire.com/fastly/7fc2f0584ac3d512ec932f9d1c3547acf2a50966e487acf011948334c71c5819.png",
      },
    }),
    strictLocalize({
      defaultMessage: "“throw blankets”",
      description: {
        text: "Placeholder for search input. The default message is the text within the quotes",
        img: "https://cdn.faire.com/fastly/7fc2f0584ac3d512ec932f9d1c3547acf2a50966e487acf011948334c71c5819.png",
      },
    }),
    strictLocalize({
      defaultMessage: "“greeting cards”",
      description: {
        text: "Placeholder for search input. The default message is the text within the quotes",
        img: "https://cdn.faire.com/fastly/7fc2f0584ac3d512ec932f9d1c3547acf2a50966e487acf011948334c71c5819.png",
      },
    }),
    strictLocalize({
      defaultMessage: "“hair clips”",
      description: {
        text: "Placeholder for search input. The default message is the text within the quotes",
        img: "https://cdn.faire.com/fastly/7fc2f0584ac3d512ec932f9d1c3547acf2a50966e487acf011948334c71c5819.png",
      },
    }),
    strictLocalize({
      defaultMessage: "“dog treats”",
      description: {
        text: "Placeholder for search input. The default message is the text within the quotes",
        img: "https://cdn.faire.com/fastly/7fc2f0584ac3d512ec932f9d1c3547acf2a50966e487acf011948334c71c5819.png",
      },
    }),
    strictLocalize({
      defaultMessage: "“vases”",
      description: {
        text: "Placeholder for search input. The default message is the text within the quotes",
        img: "https://cdn.faire.com/fastly/7fc2f0584ac3d512ec932f9d1c3547acf2a50966e487acf011948334c71c5819.png",
      },
    }),
    strictLocalize({
      defaultMessage: "“children books”",
      description: {
        text: "Placeholder for search input. The default message is the text within the quotes",
        img: "https://cdn.faire.com/fastly/7fc2f0584ac3d512ec932f9d1c3547acf2a50966e487acf011948334c71c5819.png",
      },
    }),
    strictLocalize({
      defaultMessage: "“essential oils”",
      description: {
        text: "Placeholder for search input. The default message is the text within the quotes",
        img: "https://cdn.faire.com/fastly/7fc2f0584ac3d512ec932f9d1c3547acf2a50966e487acf011948334c71c5819.png",
      },
    }),
    strictLocalize({
      defaultMessage: "“coffee beans”",
      description: {
        text: "Placeholder for search input. The default message is the text within the quotes",
        img: "https://cdn.faire.com/fastly/7fc2f0584ac3d512ec932f9d1c3547acf2a50966e487acf011948334c71c5819.png",
      },
    }),
    strictLocalize({
      defaultMessage: "“sauces”",
      description: {
        text: "Placeholder for search input. The default message is the text within the quotes",
        img: "https://cdn.faire.com/fastly/7fc2f0584ac3d512ec932f9d1c3547acf2a50966e487acf011948334c71c5819.png",
      },
    }),
  ];
};

export const SearchSuggestionAnimation = () => {
  const { strictLocalize } = useStrictLocalization();
  const animationTyping = getAnimationTypeSuggestions(strictLocalize);

  return (
    <AnimationContainer>
      <Typography color={Core.text.subdued}>
        <StrictLocalMsg
          defaultMessage="Search for"
          description={{
            text: "This is the prefix for search suggestions",
            img: "https://cdn.faire.com/fastly/7fc2f0584ac3d512ec932f9d1c3547acf2a50966e487acf011948334c71c5819.png",
          }}
        />
      </Typography>
      <AnimationPlaceholder>
        {animationTyping.map((placeholder) => (
          <li key={placeholder}>
            <Typography color={Core.text.subdued}>{placeholder}</Typography>
          </li>
        ))}
      </AnimationPlaceholder>
    </AnimationContainer>
  );
};

const AnimationContainer = styled(Row)`
  position: absolute;
  top: 10px;
  left: 20px;
  // copied as line height from rendered typography
  height: 20px;
  // As an overlay of the placeholder, it should not be interactive
  pointer-events: none;
  user-select: none;
  overflow: hidden;
`;

const scrollUpAnimation = keyframes`
  0%, 4.76% {
    transform: translateY(0%);
  }
  7.14% {
    transform: translateY(-103%);
  }
  9.52%, 14.28% {
    transform: translateY(-100%);
  }
  16.66% {
    transform: translateY(-203%);
  }
  19.04%, 23.80% {
    transform: translateY(-200%);
  }
  26.18% {
    transform: translateY(-303%);
  }
  28.56%, 33.32% {
    transform: translateY(-300%);
  }
  35.70% {
    transform: translateY(-403%);
  }
  38.08%, 42.84% {
    transform: translateY(-400%);
  }
  45.22% {
    transform: translateY(-503%);
  }
  47.60%, 52.36% {
    transform: translateY(-500%);
  }
  54.74% {
    transform: translateY(-603%);
  }
  57.12%, 61.88% {
    transform: translateY(-600%);
  }
  64.26% {
    transform: translateY(-703%);
  }
  66.64%, 71.40% {
    transform: translateY(-700%);
  }
  73.78% {
    transform: translateY(-803%);
  }
  76.16%, 80.92% {
    transform: translateY(-800%);
  }
  83.30% {
    transform: translateY(-903%);
  }
  85.68%, 90.44% {
    transform: translateY(-900%);
  }
  92.82% {
    transform: translateY(-1003%);
  }
  95.20%, 97.60% {
    transform: translateY(-1000%);
  }
  98.80% {
    transform: translateY(3%);
  }
  100% {
    transform: translateY(0%);
  }
`;

const AnimationPlaceholder = styled.ul`
  height: 100%;
  margin-left: 4px;
  // 33s is the duration of the animation, 3s per word, 11 words
  animation: ${scrollUpAnimation} 33s cubic-bezier(0.34, 1.56, 0.64, 1) infinite;
`;
